import { useApolloClient } from '@apollo/client';
import { SimpleMarkdown } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  addIssue,
  setBlackoutPeriod,
  setCurrentCarouselItem,
  setIsEmergency,
  setIssueOptionAnswer,
  setServiceCodeId,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { useAutoAdvanceEffects } from 'hooks/useAutoAdvanceEffects';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  ICarouselItemIssueDetailsBottomProps,
  IIssueResponse,
  IIssueSpecOption,
  IServiceCodeResponse,
  IState,
  IssueDetailsCarouselItem,
} from 'typings';
import isIssueDetailsItemSkipped from 'utils/isIssueDetailsItemSkipped';
import isIssueImmediatelyRedirected from 'utils/isIssueImmediatelyRedirected';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import { QuestionPillGroup } from './QuestionPillGroup';
import {
  Buttons,
  Container,
  Description,
  FormGroup,
  NoticeIconError,
  NoticeIconTip,
  NoticeText,
  SecondaryQuestion,
  Subhead,
  Warning,
  WarningIcon,
  WarningText,
} from './styles';

const issueQuery = loader('../../queries/issue.graphql');
const serviceCodeQuery = loader('../../queries/serviceCode.graphql');

export const CAROUSEL_ITEM_ID = CarouselItemId.IssueDetails;

export const CarouselItemIssueDetailsBottom = ({
  issueStackItem,
}: ICarouselItemIssueDetailsBottomProps) => {
  const apolloClient = useApolloClient();
  const dispatch = useThunkDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { buttonType, mediaUploadEnabled, repairCommentsEnabled } = useTheme();
  const { flow } = useSelector((state: IState) => ({
    flow: state.client.flow,
  }));

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  const flowCarouselItem = tryGetFlowCarouselItem<IssueDetailsCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const getSecondarySpecifier = useCallback(() => {
    const {
      issue: {
        specifier: { next = null },
      },
      optionAnswers,
    } = issueStackItem;

    if (optionAnswers.length === 0 || !next) {
      return null;
    }

    const radioOneValue = optionAnswers[0]?.value;
    const secondarySpecifier = next.find(
      ({ value }) => value === radioOneValue,
    );

    return secondarySpecifier;
  }, [issueStackItem]);

  const getIsContinueDisabled = () => {
    const {
      issue: {
        specifier: {
          details: {
            options,
            serviceCodeId: rootServiceCodeId,
            toIssueId: rootToIssueId,
          },
        },
      },
      optionAnswers,
    } = issueStackItem;

    // if there are no options, we disable the continue button if the root
    // issue did not have a serviceCodeId and also did not have a toIssueId
    if (!options || options.length === 0) {
      return !rootServiceCodeId && !rootToIssueId;
    }

    const possibleOptionValues = options.map(
      ({ nextValue, serviceCodeId, toIssueId }) =>
        serviceCodeId || nextValue || toIssueId || '',
    );

    if (
      !optionAnswers[0] ||
      !possibleOptionValues.includes(optionAnswers[0].value)
    ) {
      return true;
    }

    const secondarySpecifier = getSecondarySpecifier();

    if (!secondarySpecifier) {
      return false;
    }

    const {
      details: { options: secondaryOptions },
    } = secondarySpecifier;

    if (!secondaryOptions || secondaryOptions.length === 0) {
      return false;
    }

    const possibleSecondaryOptionValues = secondaryOptions.map(
      ({ nextValue, serviceCodeId, toIssueId }) =>
        serviceCodeId || nextValue || toIssueId || '',
    );

    if (
      !optionAnswers[1] ||
      !possibleSecondaryOptionValues.includes(optionAnswers[1].value)
    ) {
      return true;
    }

    return false;
  };

  const navigateToNextItem = useCallback(
    async (serviceCodeId: string, isEmergency: boolean) => {
      const { query } = apolloClient;

      setIsLoading(true);

      const { data: { serviceCode = null } = {} } =
        await query<IServiceCodeResponse>({
          // We never want to use a cached version of this response so that
          // blackoutPeriod is always fresh
          fetchPolicy: 'network-only',
          query: serviceCodeQuery,
          variables: {
            id: serviceCodeId,
            skip: false,
          },
        });

      setIsLoading(false);

      const blackoutPeriod = serviceCode && serviceCode.trade.blackoutPeriod;

      let destinationItemId;

      if (flowCarouselItem) {
        destinationItemId = flowCarouselItem.options.destinations.next;
      } else {
        destinationItemId = mediaUploadEnabled
          ? CarouselItemId.MediaUpload
          : repairCommentsEnabled
          ? CarouselItemId.RepairComments
          : CarouselItemId.CustomerLookup;
      }

      if (blackoutPeriod) {
        dispatch(setIsEmergency(isEmergency));
        dispatch(setServiceCodeId(serviceCodeId));
        dispatch(setBlackoutPeriod(blackoutPeriod));
        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            destinationItemId,
          ),
        );
      } else {
        dispatch(setIsEmergency(isEmergency));
        dispatch(setServiceCodeId(serviceCodeId));
        dispatch(setBlackoutPeriod());
        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            destinationItemId,
          ),
        );
      }
    },
    [
      apolloClient,
      dispatch,
      flowCarouselItem,
      mediaUploadEnabled,
      repairCommentsEnabled,
    ],
  );

  const resolveIssueId = useCallback(
    async (issueId: string | null) => {
      const { query } = apolloClient;

      if (issueId) {
        setIsLoading(true);

        const { data: { issue = null } = {} } = await query<IIssueResponse>({
          query: issueQuery,
          variables: {
            id: issueId,
          },
        });

        setIsLoading(false);

        if (issue) {
          const { serviceCodeId } = issue.specifier.details;

          if (isIssueDetailsItemSkipped(issue) && serviceCodeId) {
            const { isEmergency } = issue;

            // isIssueDetailsItemSkipped checks to make sure serviceCodeId is truthy, so no need for an extra
            // check here
            navigateToNextItem(serviceCodeId, isEmergency);
          } else if (isIssueImmediatelyRedirected(issue)) {
            dispatch(addIssue(issue));
            resolveIssueId(issue.specifier.details.toIssueId);
          } else {
            dispatch(addIssue(issue));
            /**
             * Flow special case:
             *
             * Right now the `destinations` object has a `refineIssue` field
             * that is typically `ISSUE_DETAILS` to represent the navigation
             * below. However, we see that the item we navigate to is dynamic;
             * with an `issue.id` as a suffix. So for now, even flow-based
             * widget sessions will use the original navigation code.
             */
            dispatch(
              setCurrentCarouselItem(
                CarouselReduxReducer.SERVICE,
                `${CarouselItemId.IssueDetails}-${issue.id}` as CarouselItemId,
              ),
            );
          }
        }
      }
    },
    [apolloClient, dispatch, navigateToNextItem],
  );

  const handleRadioChange = (
    questionIndex: number,
    event: React.FormEvent<HTMLInputElement>,
  ) => {
    const { issue } = issueStackItem;
    const { value: optionIndexString } = event.currentTarget;
    let nextOptions: Array<IIssueSpecOption> | null = null;

    if (questionIndex === 0) {
      nextOptions = issueStackItem.issue.specifier.details.options;
    } else if (questionIndex === 1) {
      const secondarySpecifier = getSecondarySpecifier();

      if (secondarySpecifier) {
        nextOptions = secondarySpecifier.details.options;
      }
    }

    if (nextOptions) {
      const optionIndex = parseInt(optionIndexString, 10);
      const option = nextOptions[optionIndex];

      if (option) {
        const { isEmergency, nextValue, note, serviceCodeId, toIssueId } =
          option;
        const answer = {
          index: optionIndex,
          isEmergency: isEmergency === true,
          note,
          value: serviceCodeId || nextValue || toIssueId || '',
        };

        dispatch(setIssueOptionAnswer(issue.id, questionIndex, answer));

        // If question 0's answer is changed, we want to unset question 1's answer
        if (questionIndex === 0) {
          dispatch(setIssueOptionAnswer(issue.id, 1));
        }
      }
    }
  };

  const handleContinueClick = useCallback(async () => {
    const {
      issue: {
        isEmergency,
        specifier: {
          details: { serviceCodeId, toIssueId },
        },
      },
      optionAnswers,
    } = issueStackItem;

    const options =
      getSecondarySpecifier()?.details.options ||
      issueStackItem.issue.specifier.details.options;

    const finalAnswer = optionAnswers[1] || optionAnswers[0];
    const finalValue = finalAnswer?.value || '';
    const isFinalValueServiceCode = /^sc_/.test(finalValue);
    const isFinalValueIssueId = /^issue_/.test(finalValue);

    if (isFinalValueServiceCode && options) {
      // The user's answer resulted in a serviceCodeId

      const option = options.find((item) => item.serviceCodeId === finalValue);
      const isOptionEmergency = !!(option && option.isEmergency === true);

      navigateToNextItem(finalValue, isOptionEmergency);
    } else if (isFinalValueIssueId) {
      // The user's answer resulted in an issueId

      resolveIssueId(finalValue);
    } else if (serviceCodeId) {
      // The issue has a serviceCodeId

      navigateToNextItem(serviceCodeId, isEmergency);
    } else if (toIssueId) {
      // The issue has a toIssueId

      resolveIssueId(toIssueId);
    }
  }, [
    getSecondarySpecifier,
    issueStackItem,
    navigateToNextItem,
    resolveIssueId,
  ]);

  const {
    issue: {
      isEmergency,
      specifier: { details },
    },
  } = issueStackItem;
  const { info, notice, options, warn } = details;

  const secondarySpecifier = getSecondarySpecifier();
  const isContinueDisabled = getIsContinueDisabled();

  const hasOptions = options && options.length > 0;
  const isNoteVisible = info && !secondarySpecifier;
  const isNoteVisibleRef = useRef(isNoteVisible);
  const wasNoteVisible = isNoteVisibleRef.current;
  const didUserSeeNote = wasNoteVisible || !isNoteVisible;

  useAutoAdvanceEffects(
    !!(!isContinueDisabled && hasOptions && didUserSeeNote),
    useCallback(() => {
      setIsLoading(true);
    }, []),
    useCallback(() => {
      setIsLoading(false);
      handleContinueClick();

      isNoteVisibleRef.current = isNoteVisible;
    }, [handleContinueClick, isNoteVisible]),
  );

  return (
    <FormGroup>
      <Container>
        {notice && (
          <Subhead>
            {isEmergency ? <NoticeIconError /> : <NoticeIconTip />}
            <NoticeText>{notice}</NoticeText>
          </Subhead>
        )}
        <QuestionPillGroup
          details={details}
          index={0}
          issueStackItem={issueStackItem}
          onRadioChange={handleRadioChange.bind(null, 0)}
        />
        {secondarySpecifier && (
          <SecondaryQuestion>
            <QuestionPillGroup
              details={secondarySpecifier.details}
              index={1}
              issueStackItem={issueStackItem}
              onRadioChange={handleRadioChange.bind(null, 1)}
            />
          </SecondaryQuestion>
        )}
        {isNoteVisible && (
          <Description hasNotice={!!notice}>
            <SimpleMarkdown>{info}</SimpleMarkdown>
          </Description>
        )}
        {warn && (
          <Warning>
            <WarningIcon />
            <WarningText>
              <SimpleMarkdown>{warn}</SimpleMarkdown>
            </WarningText>
          </Warning>
        )}
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          isDisabled={isContinueDisabled}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
